import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";
import { ArrowLeft, Calendar, User, Tag } from "lucide-react";
import axios from "axios";

const API = "/api";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_solar-config/artifacts/j8etyapd_ChatGPT%20Image%2026%20feb%202026%2C%2011_13_48.png";

const CATEGORY_COLORS = {
  Guida: "bg-blue-50 text-blue-700",
  Risparmio: "bg-emerald-50 text-emerald-700",
  Tecnologia: "bg-purple-50 text-purple-700",
  Incentivi: "bg-amber-50 text-amber-700",
  Consigli: "bg-rose-50 text-rose-700",
};

export default function BlogArticle() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const res = await axios.get(`${API}/blog/articles/${slug}`);
        setArticle(res.data);
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    fetchArticle();
  }, [slug]);

  // Set document title when article is loaded - must be before conditional returns
  useSEO({
    title: article ? `${article.title} | Blog Fotovoltaico Chiavi in Mano` : "Blog Fotovoltaico Chiavi in Mano",
    description: article?.excerpt || article?.title || "",
    ogType: "article",
    ogImage: article?.cover_image || "",
    canonical: article ? `${window.location.origin}/blog/${article.slug}` : "",
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (notFound || !article) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-4" data-testid="article-not-found">
        <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>Articolo non trovato</h1>
        <p className="text-slate-500 mb-6">L'articolo che cerchi non esiste o non e ancora stato pubblicato.</p>
        <button onClick={() => navigate("/blog")} className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-full px-6 py-2.5 text-sm btn-scale">
          Torna al Blog
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="blog-article-page">
      {/* Top bar */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2.5">
            <img src={LOGO_URL} alt="Fotovoltaico Chiavi in Mano" className="h-14 w-auto object-contain" />
            <span className="hidden sm:block text-lg font-bold tracking-tight text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
              Fotovoltaico Chiavi in Mano
            </span>
          </a>
          <div className="flex items-center gap-4">
            <button onClick={() => navigate("/blog")} className="text-sm text-slate-500 hover:text-emerald-600 flex items-center gap-1.5 transition-colors" data-testid="article-back-blog">
              <ArrowLeft className="w-4 h-4" /> Blog
            </button>
            <button onClick={() => navigate("/")} className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-full px-6 py-2.5 text-sm btn-scale shadow-lg shadow-emerald-500/25" data-testid="article-cta">
              Richiedi Preventivo
            </button>
          </div>
        </div>
      </div>

      {/* Article Header */}
      <header className="py-12 sm:py-16 bg-slate-50" data-testid="article-header">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <button onClick={() => navigate("/blog")} className="text-sm text-slate-400 hover:text-emerald-600 flex items-center gap-1 mb-6 transition-colors">
            <ArrowLeft className="w-3.5 h-3.5" /> Torna al blog
          </button>
          <div className="flex items-center gap-3 mb-4">
            {article.category && (
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${CATEGORY_COLORS[article.category] || "bg-slate-100 text-slate-600"}`}>
                <Tag className="w-3 h-3 inline mr-1" />
                {article.category}
              </span>
            )}
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight leading-tight" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="article-title">
            {article.title}
          </h1>
          <div className="flex items-center gap-4 mt-6 text-sm text-slate-400">
            <span className="flex items-center gap-1.5">
              <User className="w-4 h-4" /> {article.author}
            </span>
            <span className="flex items-center gap-1.5">
              <Calendar className="w-4 h-4" />
              {new Date(article.created_at).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })}
            </span>
          </div>
        </div>
      </header>

      {/* Cover Image */}
      {article.cover_image && (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-2">
          <div className="rounded-2xl overflow-hidden shadow-lg aspect-[2/1]">
            <img src={article.cover_image} alt={article.title} className="w-full h-full object-cover" data-testid="article-cover" />
          </div>
        </div>
      )}

      {/* Article Content */}
      <article className="py-12 sm:py-16" data-testid="article-content">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className="prose prose-slate prose-lg max-w-none
              prose-headings:font-bold prose-headings:tracking-tight
              prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
              prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
              prose-p:text-slate-600 prose-p:leading-relaxed
              prose-a:text-emerald-600 prose-a:no-underline hover:prose-a:underline
              prose-strong:text-slate-900
              prose-ul:text-slate-600 prose-ol:text-slate-600
              prose-li:marker:text-emerald-500
              prose-blockquote:border-emerald-500 prose-blockquote:text-slate-500
              prose-img:rounded-xl"
            style={{ fontFamily: "DM Sans, sans-serif" }}
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {/* Social Share */}
          <div className="mt-12 pt-8 border-t border-slate-100" data-testid="social-share">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-4">Condividi questo articolo</p>
            <div className="flex items-center gap-2">
              <a href={`https://wa.me/?text=${encodeURIComponent(article.title + " " + window.location.href)}`}
                target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2.5 rounded-full text-xs font-semibold bg-green-50 text-green-700 hover:bg-green-100 transition-colors"
                data-testid="share-whatsapp">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                WhatsApp
              </a>
              <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
                target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                data-testid="share-facebook">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                Facebook
              </a>
              <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`}
                target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2.5 rounded-full text-xs font-semibold bg-sky-50 text-sky-700 hover:bg-sky-100 transition-colors"
                data-testid="share-linkedin">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                LinkedIn
              </a>
              <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(article.title)}&url=${encodeURIComponent(window.location.href)}`}
                target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors"
                data-testid="share-x">
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                X
              </a>
            </div>
          </div>
        </div>
      </article>

      {/* CTA Section */}
      <section className="py-16 bg-slate-900 relative overflow-hidden" data-testid="article-cta-section">
        <div className="absolute inset-0 grain opacity-20" />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>
            Vuoi passare al fotovoltaico?
          </h2>
          <p className="text-slate-400 text-base mb-8 max-w-lg mx-auto">
            Configura il tuo impianto ideale e ricevi un preventivo gratuito dai migliori installatori della tua zona.
          </p>
          <button
            onClick={() => navigate("/")}
            className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-full px-8 py-3.5 text-sm btn-scale shadow-xl shadow-emerald-500/30"
            data-testid="article-final-cta"
          >
            Configura il Tuo Impianto
          </button>
        </div>
      </section>

      <footer className="py-8 bg-slate-950 text-center">
        <p className="text-xs text-slate-500">© 2025 Fotovoltaico Chiavi in Mano. Tutti i diritti riservati.</p>
      </footer>
    </div>
  );
}
